import { useAuth } from '@agentnet/auth';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenPaymentAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import useAsync from 'hooks/useAsync';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AsyncStatus } from '../constants';
import { FincenUpdateResponse, PaymentInformation } from '../types';
import { PayorType } from './constants';
import { getInitialPaymentInformationData } from './models/payment';
import PaymentInformationContent from './PaymentInformationContent';

interface RouteParams {
  fileId: string;
}

const PaymentInformationContainer: FC = () => {
  const { addSnackbarMessage } = useSnackBars();
  const { fileId } = useParams<RouteParams>();
  const history = useHistory();
  const { getAccessToken } = useAuth();

  const fileDataCtx = useContext(FileDataContext) as FileDataContextInterface;
  const { fileData } = fileDataCtx;

  // Load file data when lost on refresh
  useEffect(() => {
    if (fileId && fileDataCtx && !fileDataCtx.fileData?.fileId && fileDataCtx.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataCtx.loadFileData(fileProps);
    }
  }, [fileId, fileDataCtx]);

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const {
    fincenReport,
    fincenReportStatus,
    updatePaymentInformation,
    paymentValidationErrors,
    clearPaymentValidationErrors,
  } = fincenReportCtx ?? {};

  const [formData, setFormData] = useState<PaymentInformation>(getInitialPaymentInformationData());
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [shouldNavigateOnComplete, setShouldNavigateOnComplete] = useState(false);
  const isNavigating = useNavigationLoading();

  const cleanPaymentDataForAPI = (data: PaymentInformation): PaymentInformation => {
    return {
      ...data,
      payments: data.payments.map((payment) => ({
        ...payment,
        // If "Payment associated with all recorded transferees" is ON, send empty array
        associatedTransfereeNames: payment.associatedWithAllTransferees ? [] : payment.associatedTransfereeNames,
        // If "Payment not from financial institution account" is ON (fromFinancialInstitution is false), clear account fields
        accountNumber: payment.fromFinancialInstitution ? payment.accountNumber : '',
        financialInstitutionName: payment.fromFinancialInstitution ? payment.financialInstitutionName : '',
        // If "All payors are recorded transferees" is ON, send empty array
        payors: payment.allPayorsAreTransferees
          ? []
          : payment.payors.map((payor) => {
              if (payor.type === PayorType.INDIVIDUAL) {
                // For Individual, only include Individual fields, remove Entity fields
                const { legalName, ...individualFields } = payor as any;
                return individualFields;
              } else {
                // For Entity, only include Entity fields, remove Individual fields
                const { firstName, middleName, lastName, suffix, ...entityFields } = payor as any;
                return entityFields;
              }
            }),
      })),
    };
  };

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReport) return;
    if (fincenReport.reportStatusCdId === 9302) return; // Disable auto-save once report generated
    const token = await getAccessToken();
    const cleanedData = cleanPaymentDataForAPI(formData);
    await updateFincenPaymentAPI(String(fincenReport.fincenReportId || 0), cleanedData, token);
  }, [fincenReport, getAccessToken, formData]);

  useAutoSaveNavigation('payment-information', handleAutoSave, fileId, fincenReport?.reportStatusCdId !== 9302);

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: formData,
    updateFunction: updatePaymentInformation,
    apiSaveFunction: updateFincenPaymentAPI,
    reportId: fincenReport?.fincenReportId,
    hasContent: (data) => data.payments.some((p) => p.amount || p.paymentMethod || p.payors.length > 0),
    transformDataForAPI: cleanPaymentDataForAPI,
    enabled: fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'payment information',
  });

  useEffect(() => {
    if (fincenReport?.paymentDetails) {
      // Check if any payment needs normalization (toggle OFF but no transferee names)
      const needsNormalization = fincenReport.paymentDetails.payments.some(
        (payment) => !payment.associatedWithAllTransferees && payment.associatedTransfereeNames.length === 0,
      );

      if (needsNormalization) {
        // Normalize payment data: ensure associatedTransfereeNames has at least one entry when toggle is OFF
        const normalizedPaymentDetails = {
          ...fincenReport.paymentDetails,
          payments: fincenReport.paymentDetails.payments.map((payment) => ({
            ...payment,
            // If "Payment associated with all recorded transferees" is OFF but no transferee names exist,
            // initialize with an empty string to show the input field
            associatedTransfereeNames:
              !payment.associatedWithAllTransferees && payment.associatedTransfereeNames.length === 0
                ? ['']
                : payment.associatedTransfereeNames,
          })),
        };
        setFormData(normalizedPaymentDetails);

        // Update context with normalized data so validation runs against the correct data structure
        // This ensures validation errors are properly generated for associatedTransfereeNames[0]
        if (updatePaymentInformation) {
          updatePaymentInformation(normalizedPaymentDetails);
        }
      } else {
        setFormData(fincenReport.paymentDetails);
      }
    }
  }, [fincenReport?.paymentDetails, updatePaymentInformation]);

  const updateFincenPaymentData = async (): Promise<FincenUpdateResponse> => {
    const token = await getAccessToken();
    const cleanedData = cleanPaymentDataForAPI(formData);
    return updateFincenPaymentAPI(String(fincenReport?.fincenReportId || 0), cleanedData, token);
  };

  const {
    execute: executeUpdateFincenPaymentData,
    value: updateFincenPaymentResponse,
    status: updateFincenPaymentStatus,
    errors: updateFincenPaymentErrors,
  } = useAsync<FincenUpdateResponse>(updateFincenPaymentData, false);

  useEffect(() => {
    if (formSubmitted) {
      executeUpdateFincenPaymentData();
    }
  }, [formSubmitted]);

  useEffect(() => {
    if (updateFincenPaymentStatus === AsyncStatus.SUCCESS) {
      if (shouldNavigateOnComplete) {
        setShouldNavigateOnComplete(false);
        history.push(`/files/${fileId}/fincen-reporting/certify-generate`);
      } else {
        addSnackbarMessage({
          message: 'Saving Successful',
          type: 'success',
        });
      }
      fincenReportCtx?.reloadFincenReport?.(fileData?.fileId ?? '0');
      setFormSubmitted(false);
    }
  }, [updateFincenPaymentStatus, shouldNavigateOnComplete]);

  const handleSubmissionComplete = (forceNavigate?: boolean) => {
    setFormSubmitted(false);
    if (forceNavigate || shouldNavigateOnComplete) {
      setShouldNavigateOnComplete(false);
      history.push(`/files/${fileId}/fincen-reporting/certify-generate`);
    }
  };

  const handleNext = () => {
    if (fincenReport?.reportStatusCdId === 9302) {
      history.push(`/files/${fileId}/fincen-reporting/certify-generate`);
      return;
    }
    setShouldNavigateOnComplete(true);
    setFormSubmitted(true);
  };

  const handleBack = () => {
    history.push(`/files/${fileId}/fincen-reporting/transferor-info`);
  };

  const handleSaveDraft = async () => {
    setFormSubmitted(true);
  };

  const handleFormChange = (updatedData: PaymentInformation) => {
    setFormData(updatedData);

    if (updatePaymentInformation) {
      updatePaymentInformation(updatedData);
    }
  };

  // Redirect to files if fileId is not present (handles browser refresh edge case)
  if (!fileId) {
    return <Redirect to="/files" />;
  }

  return (
    <>
      <LoadingSpinner
        status={formSubmitted || isNavigating || fincenReportStatus === 'pending' ? 'pending' : 'success'}
        variant="linear"
      />
      <PaymentInformationContent
        paymentInformation={formData}
        onChange={handleFormChange}
        isReadOnly={fincenReport?.reportStatusCdId === 9302}
      />
      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBack}
        cancelDataQa="fincen-payment-back-button"
        secondaryActionLabel="Save Report"
        onSecondaryClick={handleSaveDraft}
        secondaryActionDataQa="fincen-payment-save-button"
        primaryActionLabel="Next"
        onPrimaryClick={handleNext}
        primaryActionDataQa="fincen-payment-next-button"
        disableSecondary={fincenReport?.reportStatusCdId === 9302}
      />
    </>
  );
};

export default PaymentInformationContainer;
